import React, { useState, useEffect, useRef } from "react";
import UnverifiedPrompt from "./UnverifiedPrompt";
import GoogleSignInButton from "./GoogleSignInButton";

function SignUpModal({
  onClose,
  onEmailSignUp,
  onSwitchToLogin,
  onAppleLogin,
  onGoogleLogin,
  onEmailLogin,
  RESEND_VERIFICATION_ENDPOINT,
}) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");

  const [emailPulse, setEmailPulse] = useState(false);
  const [passwordPulse, setPasswordPulse] = useState(false);

  // "form": show sign-up fields; "unverified": show unverified prompt.
  const [stage, setStage] = useState("form");
  const [resendStatus, setResendStatus] = useState("");

  // Ref for the password field (for Enter key behavior)
  const passwordInputRef = useRef(null);

  // --- Styles ---
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0,0,0,0.5)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    zIndex: 2000,
  };

  const modalStyle = {
    background: "#fff",
    borderRadius: "12px",
    width: "360px",
    padding: "30px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  };

  const headingStyle = {
    fontSize: "24px",
    fontWeight: "600",
    marginBottom: "5px",
    color: "#333",
  };

  const subheadingStyle = {
    fontSize: "14px",
    color: "#666",
    marginBottom: "20px",
  };

  const appleButtonContainerStyle = {
    display: "flex",
    justifyContent: "center",
    marginBottom: "15px",
  };

  const appleButtonStyle = {
    width: "210px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "8px",
    border: "1px solid #000",
    borderRadius: "8px",
    background: "#000",
    color: "#fff",
    fontSize: "15px",
    fontWeight: "500",
    cursor: "pointer",
  };

  const orContainerStyle = {
    display: "flex",
    alignItems: "center",
    margin: "15px 0",
  };

  const hrStyle = {
    flex: 1,
    border: "none",
    height: "1px",
    backgroundColor: "#ddd",
  };

  const orTextStyle = {
    margin: "0 10px",
    color: "#999",
    fontSize: "14px",
  };

  const baseInputStyle = {
    width: "100%",
    height: "40px",
    fontSize: "16px",
    padding: "8px",
    marginBottom: "5px",
    border: "1px solid #ccc",
    borderRadius: "6px",
    boxSizing: "border-box",
  };

  const signUpButtonStyle = {
    width: "100%",
    borderRadius: "8px",
    backgroundColor: "#28a745",
    color: "#fff",
    fontSize: "16px",
    padding: "12px",
    border: "none",
    cursor: "pointer",
    marginBottom: "15px",
  };

  const footerTextStyle = {
    textAlign: "center",
    fontSize: "14px",
    color: "#555",
  };

  const linkStyle = {
    fontSize: "14px",
    color: "#007bff",
    textDecoration: "none",
    cursor: "pointer",
  };

  const closeButtonStyle = {
    width: "100%",
    borderRadius: "8px",
    backgroundColor: "#ddd",
    color: "#333",
    fontSize: "16px",
    padding: "12px",
    border: "none",
    cursor: "pointer",
    marginTop: "15px",
  };

  const errorTextStyle = {
    color: "red",
    fontSize: "12px",
    marginBottom: "15px",
  };

  const labelContainerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "5px",
  };

  const labelStyle = {
    fontWeight: "500",
    fontSize: "14px",
    color: "#333",
  };

  // --- Handlers ---
  const handleOverlayClick = () => {
    // Only allow closing if not in verification stage.
    if (stage !== "unverified") {
      onClose();
    }
  };

  const handleContainerClick = (e) => {
    e.stopPropagation();
  };

  const triggerPulse = (field) => {
    if (field === "email") {
      setEmailPulse(true);
      setTimeout(() => setEmailPulse(false), 1000);
    }
    if (field === "password") {
      setPasswordPulse(true);
      setTimeout(() => setPasswordPulse(false), 1000);
    }
  };

  // When the form is submitted, validate inputs and attempt sign-up.
  const handleSubmit = async (e) => {
    e.preventDefault();
    let valid = true;
    if (!email.trim()) {
      setEmailError("Email is required");
      triggerPulse("email");
      valid = false;
    } else {
      setEmailError("");
    }
    if (!password.trim()) {
      setPasswordError("Password is required");
      triggerPulse("password");
      valid = false;
    } else {
      setPasswordError("");
    }
    if (valid) {
      const result = await onEmailSignUp(email, password);
      if (result.success) {
        // Switch to the verification prompt screen.
        setStage("unverified");
      } else {
        alert("Signup failed: " + result.error);
      }
    }
  };

  // --- Key Down Handlers ---
  // For the email field: if both fields are valid, submit; otherwise, move focus to the password field.
  const handleEmailKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (!email.trim()) {
        // Do nothing if email is not valid.
        return;
      }
      if (email.trim() && password.trim()) {
        handleSubmit(e);
      } else if (passwordInputRef.current) {
        passwordInputRef.current.focus();
      }
    }
  };

  // For the password field: always submit on Enter.
  const handlePasswordKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const getInputStyle = (error, pulse) => ({
    ...baseInputStyle,
    border: error ? "1px solid red" : baseInputStyle.border,
    animation: pulse ? "pulse 1s" : "none",
  });

  useEffect(() => {
    if (stage === "unverified" && email) {
      const intervalId = setInterval(async () => {
        try {
          const response = await fetch(
            `https://developer-ai.com/devaix/dev_ai_login/is-verified?email=${encodeURIComponent(email)}`
          );
          const data = await response.json();
          if (data.success && data.is_verified) {
            clearInterval(intervalId);
            // Once verified, attempt to log in again automatically.
            const result = await onEmailLogin(email, password);
            if (result.success && result.is_verified === 1) {
              // alert("Your email has been verified! Logging you in automatically."); // give in browser notif
              onClose();
            }
          }
        } catch (err) {
          console.error("Error checking verification status:", err);
        }
      }, 10000); // Check every 10 seconds.
      return () => clearInterval(intervalId);
    }
  }, [stage, email, password, onEmailLogin, onClose]);

  // Handle "Resend Verification" click.
  const handleResendVerification = async () => {
    setResendStatus("");
    console.log("Resending verification email for:", email);
    try {
      const response = await fetch(RESEND_VERIFICATION_ENDPOINT, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      console.log("Resend response status:", response.status);
      const data = await response.json();
      console.log("Resend response data:", data);
      if (data.success) {
        setResendStatus("Verification link resent. Check your inbox again!");
      } else {
        setResendStatus("Could not resend verification link: " + data.error);
      }
    } catch (err) {
      console.error("Error during resend verification:", err);
      setResendStatus("An error occurred: " + err.toString());
    }
  };

  // Renders the sign-up form.
  const renderForm = () => (
    <form onSubmit={handleSubmit}>
      <h2 style={headingStyle}>Create an Account</h2>
      <div style={labelContainerStyle}>
        <label style={labelStyle}>Email Address</label>
      </div>
      <input
        type="email"
        value={email}
        onChange={(e) => {
          setEmail(e.target.value);
          if (e.target.value.trim()) {
            setEmailError("");
          }
        }}
        onKeyDown={handleEmailKeyDown}
        style={getInputStyle(emailError, emailPulse)}
      />
      {emailError && <div style={errorTextStyle}>{emailError}</div>}
      <div style={labelContainerStyle}>
        <label style={labelStyle}>Password</label>
      </div>
      <input
        type="password"
        value={password}
        ref={passwordInputRef}
        onChange={(e) => {
          setPassword(e.target.value);
          if (e.target.value.trim()) {
            setPasswordError("");
          }
        }}
        onKeyDown={handlePasswordKeyDown}
        style={getInputStyle(passwordError, passwordPulse)}
      />
      {passwordError && <div style={errorTextStyle}>{passwordError}</div>}
      <button style={signUpButtonStyle} type="submit">
        Sign Up
      </button>
      <p style={footerTextStyle}>
        Already have an account?{" "}
        <span style={linkStyle} onClick={onSwitchToLogin}>
          Log in
        </span>
      </p>
      <div style={orContainerStyle}>
        <hr style={hrStyle} />
        <span style={orTextStyle}>OR</span>
        <hr style={hrStyle} />
      </div>
      {/* Apple Sign In Button */}
      <div style={appleButtonContainerStyle}>
        <button
          type="button"
          style={appleButtonStyle}
          onClick={onAppleLogin}
          aria-label="Sign in with Apple"
        >
          <span aria-hidden="true" style={{ fontSize: "19px", lineHeight: 1 }}></span>
          Sign in with Apple
        </button>
      </div>
      <div style={appleButtonContainerStyle}>
        <GoogleSignInButton onCredential={onGoogleLogin} text="signup_with" />
      </div>
      <button style={closeButtonStyle} onClick={onClose}>
        Close
      </button>
    </form>
  );

  // Renders the verification prompt using UnverifiedPrompt.
  const renderVerificationSent = () => (
    <UnverifiedPrompt 
      email={email} 
      onResend={handleResendVerification} 
      resendStatus={resendStatus} 
      onClose={onClose} 
    />
  );

  return (
    <div style={overlayStyle} onClick={handleOverlayClick}>
      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); }
        }
      `}</style>
      <div style={modalStyle} onClick={handleContainerClick}>
        {stage === "form" ? renderForm() : renderVerificationSent()}
      </div>
    </div>
  );
}

export default SignUpModal;
